import React from "react";
import { Box, Button, TextField, Typography } from "@mui/material";
import { useNavigate, useParams } from "react-router-dom";
import { LuArrowLeft, LuPlus, LuSearch } from "react-icons/lu";
import {
  OFF_WHITE,
  DARK_NAVY,
  DARK_NAVY_LIGHT,
  filterFieldSx,
  PROFILE_FONT,
} from "../../constants/layout";
import {
  bodyCellSx,
  cardSx,
  schemeHeadCellSx,
  primaryButtonSx,
  SortableHeader,
  EmptyTableRow,
  getReturnColor,
} from "../AssetListing/assetListingShared";

export { bodyCellSx, SortableHeader, EmptyTableRow, getReturnColor };
export const headCellSx = schemeHeadCellSx;

export const holdingsPaginationSx = {
  "& .MuiTablePagination-toolbar": {
    fontFamily: PROFILE_FONT,
    color: DARK_NAVY_LIGHT,
    px: { xs: 1, md: 2 },
  },
  "& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows": {
    fontFamily: PROFILE_FONT,
    fontSize: 13,
  },
};

export const HoldingsPageShell = ({
  title,
  subtitle,
  searchValue,
  onSearchChange,
  searchPlaceholder = "Search holdings...",
  action,
  children,
}: {
  title: string;
  subtitle?: string;
  searchValue?: string;
  onSearchChange?: (value: string) => void;
  searchPlaceholder?: string;
  action?: React.ReactNode;
  children: React.ReactNode;
}) => {
  const navigate = useNavigate();
  const { id } = useParams();

  return (
    <Box
      sx={{
        minHeight: "100%",
        bgcolor: OFF_WHITE,
        px: { xs: 3, sm: 5, md: 6, lg: 8 },
        py: { xs: 3, md: 4 },
        fontFamily: PROFILE_FONT,
      }}
    >
      <Box sx={{ maxWidth: 1280, mx: "auto" }}>
        <Typography sx={{ fontFamily: PROFILE_FONT, fontSize: 13, color: DARK_NAVY_LIGHT, mb: 2 }}>
          Holdings{" "}
          <Box component="span" sx={{ mx: 0.75 }}>
            /
          </Box>
          <Box
            component="span"
            sx={{ cursor: "pointer", "&:hover": { color: DARK_NAVY } }}
            onClick={() => navigate("/holdings")}
          >
            User List
          </Box>
          <Box component="span" sx={{ mx: 0.75 }}>
            /
          </Box>
          <Box
            component="span"
            sx={{ cursor: "pointer", "&:hover": { color: DARK_NAVY } }}
            onClick={() => id && navigate(`/preferences?userId=${id}`)}
          >
            Preferences
          </Box>
          <Box component="span" sx={{ mx: 0.75 }}>
            /
          </Box>
          <Box component="span" sx={{ color: DARK_NAVY, fontWeight: 600 }}>
            {title}
          </Box>
        </Typography>

        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: { xs: "flex-start", sm: "center" },
            flexDirection: { xs: "column", sm: "row" },
            gap: 2,
            mb: 3,
          }}
        >
          <Box>
            <Typography
              sx={{
                fontFamily: PROFILE_FONT,
                fontSize: { xs: 24, md: 28 },
                fontWeight: 700,
                color: DARK_NAVY,
                mb: 0.5,
              }}
            >
              {title}
            </Typography>
            {subtitle && (
              <Typography sx={{ fontFamily: PROFILE_FONT, fontSize: 14, color: DARK_NAVY_LIGHT }}>
                {subtitle}
              </Typography>
            )}
          </Box>

          <Button
            onClick={() => (id ? navigate(`/preferences?userId=${id}`) : navigate(-1))}
            startIcon={<LuArrowLeft size={16} />}
            sx={{
              fontFamily: PROFILE_FONT,
              textTransform: "none",
              fontWeight: 600,
              fontSize: 14,
              color: DARK_NAVY_LIGHT,
              borderRadius: "10px",
              px: 2,
              py: 1,
              "&:hover": { bgcolor: "rgba(0,0,0,0.04)" },
            }}
          >
            Back
          </Button>
        </Box>

        {(onSearchChange || action) && (
          <Box sx={{ ...cardSx, p: { xs: 2, md: 2.5 }, mb: 2.5 }}>
            <Box
              sx={{
                display: "flex",
                flexDirection: { xs: "column", sm: "row" },
                gap: 2,
                alignItems: { xs: "stretch", sm: "center" },
                justifyContent: "space-between",
              }}
            >
              {onSearchChange && (
                <TextField
                  placeholder={searchPlaceholder}
                  value={searchValue ?? ""}
                  onChange={(e) => onSearchChange(e.target.value)}
                  fullWidth
                  size="small"
                  InputProps={{
                    startAdornment: (
                      <Box sx={{ display: "flex", alignItems: "center", pr: 1, color: DARK_NAVY_LIGHT }}>
                        <LuSearch size={18} />
                      </Box>
                    ),
                  }}
                  sx={{
                    ...filterFieldSx,
                    maxWidth: { sm: 420 },
                    "& .MuiOutlinedInput-root": {
                      ...filterFieldSx["& .MuiOutlinedInput-root"],
                      bgcolor: "#ffffff",
                    },
                  }}
                />
              )}
              {action}
            </Box>
          </Box>
        )}

        <Box sx={{ ...cardSx, overflow: "hidden" }}>{children}</Box>
      </Box>
    </Box>
  );
};

export const HoldingsAddButton = ({ onClick, label = "Add New" }: { onClick: () => void; label?: string }) => (
  <Button onClick={onClick} startIcon={<LuPlus size={18} />} sx={primaryButtonSx}>
    {label}
  </Button>
);

export const centerBodyCellSx = { ...bodyCellSx, textAlign: "center" as const };
export const centerHeadCellSx = { ...schemeHeadCellSx, textAlign: "center" as const };

export const gainLossCellSx = (value: string | number | null | undefined) => ({
  ...centerBodyCellSx,
  color: getReturnColor(value),
  fontWeight: 600,
});
